import locale from "@bonliva-crm/locale";
import {
  EmailEditorFormValues,
  emailEditorFormSchema
} from "@consultants/consultant-profile-new/email-editor/form-values";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { EmailAction } from "../components/actions/email";
import { ExternalActionStepProps } from "../utils/action-step-props";
import { BulkActionHookReturn } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { useBulkActions } from "./use-bulk-actions";
import { BulkActionCommunicationSubType } from "@bonliva-crm/shared";

type Props<T extends SelectionStepRowType> = {
  valueParser: ExternalActionStepProps<T>["valueParsers"]["ConsultantEmail"];
  selectedRows: T[];
  totalRows: number;
  entitySingular: string;
  entityPlural: string;
  subType?: BulkActionCommunicationSubType | null;
};

export const useConsultantEmailAction = <T extends SelectionStepRowType>(
  props: Props<T>
): BulkActionHookReturn => {
  const classes = useStyles();
  const { valueParser, selectedRows, totalRows, entitySingular, entityPlural, subType } =
    props;

  const parsedValues = useMemo(
    () => valueParser(selectedRows),
    [valueParser, selectedRows]
  );

  const emailActionForm = useForm<EmailEditorFormValues>({
    defaultValues: {
      recipients: parsedValues.recipients,
      subject: null,
      body: null,
      attachments: []
    },
    mode: "onChange",
    resolver: yupResolver(emailEditorFormSchema)
  });

  const { sendConsultantEmail } = useBulkActions({});

  const actionContent = <EmailAction form={emailActionForm} />;

  const rowsWithValidEmail = parsedValues.recipients.length;

  const confirmationContent = (
    <Box className={classes.confirmationContent}>
      {totalRows !== rowsWithValidEmail && (
        <Typography variant="body1">
          {locale.bulkActionEmailNotValid(
            totalRows,
            rowsWithValidEmail,
            entitySingular,
            entityPlural,
          )}
        </Typography>
      )}
      <Typography variant="body1">
        {locale.bulkActionConfirmation(
          `${locale.SendEmail} ${locale.To}`,
          rowsWithValidEmail,
          entitySingular,
          entityPlural
        )}
      </Typography>
      <Typography variant="body1">{locale.youWillBeNotifiedViaEmail}</Typography>
    </Box>
  );
  const onSubmit = emailActionForm.handleSubmit((data) =>
    sendConsultantEmail({
      body: data.body,
      subject: data.subject,
      attachmentIds: data.attachments.map((a) => a.NodeID),
      consultantIds: parsedValues.nodeIds,
      subType: subType ?? undefined
    })
  );

  const reset = () => {
    emailActionForm.reset();
  };

  useEffect(() => {
    emailActionForm.setValue("recipients", parsedValues.recipients);
  }, [parsedValues.recipients, emailActionForm]);

  const nextStepDisabled = !emailActionForm.formState.isValid;

  return {
    actionContent,
    confirmationContent,
    onSubmit,
    reset,
    nextStepDisabled
  };
};

const useStyles = makeStyles((theme) => ({
  confirmationContent: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }
}));
